import { useEffect, useRef } from 'react';

import { storageSessionCredentials } from '@gem/modules/storage';
import { ONE_MINUTE } from '@gem/services/date-service';

import { remoteStorageConfiguration } from '../../configuration';
import { cancelLoadRemoteStorageCredentials, loadRemoteStorageCredentials } from '../../tasks';

interface IRemoteStorageCredentialsUpdaterPresenterParams {
  isAuthenticated: boolean;
}

interface IRemoteStorageCredentialsUpdaterPresenter {
  loadFileStorageSessionCredentials(): Promise<boolean>;
  checkStorageCredentialsExpired(): boolean;
  scheduleCredentialsCheck(): void;
  dispose(): void;
}

export function useRemoteStorageCredentialsUpdaterPresenter(
  params: IRemoteStorageCredentialsUpdaterPresenterParams
): void {
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const presenter: IRemoteStorageCredentialsUpdaterPresenter = {
    checkStorageCredentialsExpired(): boolean {
      const credentials = storageSessionCredentials.get();
      if (!credentials?.expiration) {
        return false;
      }

      const expirationDate = new Date(credentials.expiration).getTime();
      const now = new Date().getTime();
      const expireIn = expirationDate - now;
      return expireIn < ONE_MINUTE;
    },

    async loadFileStorageSessionCredentials(): Promise<boolean> {
      return loadRemoteStorageCredentials();
    },

    scheduleCredentialsCheck(): void {
      timer.current = setTimeout(async () => {
        if (presenter.checkStorageCredentialsExpired()) {
          await presenter.loadFileStorageSessionCredentials();
        }

        presenter.scheduleCredentialsCheck();
      }, remoteStorageConfiguration.credentialsUpdateInterval);
    },

    dispose(): void {
      cancelLoadRemoteStorageCredentials();

      if (!timer.current) {
        return;
      }
      clearTimeout(timer.current);
    },
  };

  useEffect(() => {
    if (!params.isAuthenticated) {
      return presenter.dispose();
    }
    presenter.scheduleCredentialsCheck();
  }, [params.isAuthenticated]);

  useEffect(() => {
    return () => {
      presenter.dispose();
    };
  }, []);
}
